import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../utils/api';
import { PageHeader, Badge, Loading, formatDate, ouvrirDocument, telechargerDocument, BackButton } from '../components/Shared';
import { useAuth } from '../context/AuthContext';

const TYPES_DOCS_DEFAUTS = ['Aptitude médicale', 'Attestation de formation', 'Diplôme / Certification', "Carte d'identité", 'CV', 'Contrat', 'Permis de conduire', 'Habilitation', 'Autre'];

function docStatutStagiaire(dateFinValidite) {
  if (!dateFinValidite) return null;
  const fin = new Date(dateFinValidite);
  const now = new Date();
  const diffDays = Math.round((fin - now) / 86400000);
  if (diffDays < 0)  return { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   label: 'Expirée' };
  if (diffDays < 30) return { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: `Expire dans ${diffDays}j` };
  return { color: '#16a34a', bg: 'rgba(22,163,74,0.1)', label: `Valide jusqu'au ${new Date(dateFinValidite).toLocaleDateString('fr-FR')}` };
}

function fmtTaille(octets) {
  if (!octets) return '';
  if (octets < 1024) return `${octets} o`;
  if (octets < 1048576) return `${Math.round(octets/1024)} Ko`;
  return `${(octets/1048576).toFixed(1)} Mo`;
}

function SectionDocuments({ stagiaireId, stagiaire }) {
  const { token } = useAuth();
  const inputRef = useRef();
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typesDocs, setTypesDocs] = useState(TYPES_DOCS_DEFAUTS);
  const [typesDuree, setTypesDuree] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [iaConfiance, setIaConfiance] = useState(null);
  const [iaNomAlert, setIaNomAlert] = useState(false);
  const [addForm, setAddForm] = useState({ type_document: TYPES_DOCS_DEFAUTS[0], date_emission: '', date_fin_validite: '', notes: '' });
  const [editDoc, setEditDoc] = useState(null);
  const inputStyle = { padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };

  useEffect(() => {
    fetch(`${API_BASE}/parametres`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const raw = data?.types_docs_stagiaires;
        if (raw) {
          try {
            const arr = JSON.parse(raw);
            const names = arr.map(item => typeof item === 'string' ? item : item.nom);
            if (names.length) { setTypesDocs(names); setAddForm(f => ({ ...f, type_document: names[0] })); }
            const dm = {};
            arr.forEach(item => { if (typeof item === 'object' && item.nom && item.duree_mois) dm[item.nom] = parseInt(item.duree_mois); });
            setTypesDuree(dm);
          } catch {}
        }
      }).catch(() => {});
  }, [API_BASE, token]);

  const chargerDocs = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setDocs(Array.isArray(data) ? data : []);
    } catch { setDocs([]); }
    finally { setLoading(false); }
  }, [stagiaireId, token, API_BASE]);

  useEffect(() => { chargerDocs(); }, [chargerDocs]);

  function resetPending() {
    setPendingFile(null); setIaConfiance(null); setIaNomAlert(false);
    setShowPreview(false);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  }

  async function analyserDocument(file) {
    const analysable = file.type === 'application/pdf' || file.type.startsWith('image/');
    setPendingFile(file);
    setIaConfiance(null); setIaNomAlert(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(analysable ? URL.createObjectURL(file) : null);
    setShowPreview(false);
    if (!analysable) return;
    return; // Analyse IA désactivée (ANTHROPIC_API_KEY non configurée)
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('stagiaire_id', stagiaireId);
      const res = await fetch(`${API_BASE}/ia/analyser-document`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) {
        if (res.status === 500 || res.status === 503) {
          const err = await res.json().catch(() => ({}));
          if (err.error?.includes('Anthropic') || err.error?.includes('configurée')) return;
        }
        return;
      }
      const data = await res.json();
      if (data.non_analysable) return;
      let dateFin = data.date_fin_validite || '';
      if (!dateFin && data.date_emission && typesDuree[data.type_document]) {
        try { const d = new Date(data.date_emission); d.setMonth(d.getMonth() + typesDuree[data.type_document]); dateFin = d.toISOString().slice(0,10); } catch {}
      }
      setAddForm({ type_document: data.type_document || typesDocs[0], date_emission: data.date_emission || '', date_fin_validite: dateFin, notes: data.notes || '' });
      setIaConfiance(data.confiance || null);
      setIaNomAlert(data.nom_beneficiaire_ok === false);
    } catch {} finally { setAnalysing(false); }
  }

  async function upload(file) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('type_document', addForm.type_document);
      if (addForm.date_emission)     fd.append('date_emission', addForm.date_emission);
      if (addForm.date_fin_validite) fd.append('date_fin_validite', addForm.date_fin_validite);
      if (addForm.notes)             fd.append('notes', addForm.notes);
      const r = await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setDocs(prev => [data, ...prev]);
      setAddForm({ type_document: typesDocs[0], date_emission: '', date_fin_validite: '', notes: '' });
      resetPending();
    } catch(e) { alert('Erreur upload : ' + e.message); }
    finally { setUploading(false); }
  }

  async function saveEdit() {
    try {
      const r = await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents/${editDoc.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type_document: editDoc.type_document, date_emission: editDoc.date_emission || null, date_fin_validite: editDoc.date_fin_validite || null, notes: editDoc.notes || null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setDocs(prev => prev.map(d => d.id === editDoc.id ? data : d));
      setEditDoc(null);
    } catch(e) { alert('Erreur : ' + e.message); }
  }

  async function supprimerDoc(docId) {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents/${docId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch(e) { alert('Erreur : ' + e.message); }
  }

  function docUrl(id) { return `/stagiaires/${stagiaireId}/documents/${id}/download`; }

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <h3 className="card-title">📎 Documents administratifs</h3>

      {/* Formulaire d'ajout */}
      <div style={{ marginBottom: '0.8rem' }}>
        <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Déposer un nouveau document</p>
        <div className="grid-2" style={{ gap: '0 0.8rem', marginBottom: '0.6rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Type de document</label>
            <select value={addForm.type_document}
              onChange={e => {
                const selected = e.target.value;
                let dateFin = addForm.date_fin_validite;
                if (!dateFin && typesDuree[selected]) {
                  try { const d = new Date(); d.setMonth(d.getMonth() + typesDuree[selected]); dateFin = d.toISOString().slice(0,10); } catch {}
                }
                setAddForm({ ...addForm, type_document: selected, date_fin_validite: dateFin });
              }}
              style={inputStyle}>
              {typesDocs.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Date d'émission</label>
            <input type="date" value={addForm.date_emission} onChange={e => setAddForm({ ...addForm, date_emission: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Date de fin de validité</label>
            <input type="date" value={addForm.date_fin_validite} onChange={e => setAddForm({ ...addForm, date_fin_validite: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Notes</label>
            <input value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} placeholder="Notes optionnelles..." style={inputStyle} />
          </div>
        </div>

        {/* Carte de proposition IA + boutons */}
        {pendingFile && !analysing && !uploading && (
          <div style={{ border: `1px solid ${iaNomAlert ? 'rgba(239,68,68,0.4)' : iaConfiance ? 'rgba(232,119,34,0.4)' : 'var(--border)'}`, borderRadius: '10px', padding: '0.9rem 1rem', marginBottom: '0.8rem', background: iaNomAlert ? 'rgba(239,68,68,0.04)' : 'var(--bg-elevated)' }}>
            {iaConfiance && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: iaConfiance === 'haute' ? '#16a34a' : iaConfiance === 'moyenne' ? '#d97706' : '#dc2626' }}>
                <span>🤖</span><span>Analyse IA — confiance <strong>{iaConfiance === 'haute' ? 'haute ✓' : iaConfiance === 'moyenne' ? 'moyenne ⚠' : 'faible ✕'}</strong></span>
              </div>
            )}
            {iaNomAlert && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', padding: '0.45rem 0.65rem', borderRadius: '6px', marginBottom: '0.6rem', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', fontSize: '0.79rem', color: '#dc2626' }}>
                <span>⚠️</span><span>Le nom figurant sur ce document ne semble pas correspondre à ce stagiaire. Vérifiez qu'il s'agit bien du bon document.</span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem' }}>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Type : </span><strong>{addForm.type_document}</strong></div>
              <div><span style={{ color: 'var(--text-tertiary)' }}>Expire : </span><strong style={{ color: addForm.date_fin_validite ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>{addForm.date_fin_validite ? new Date(addForm.date_fin_validite).toLocaleDateString('fr-FR') : '—'}</strong></div>
              {addForm.date_emission && <div><span style={{ color: 'var(--text-tertiary)' }}>Émis : </span><span>{new Date(addForm.date_emission).toLocaleDateString('fr-FR')}</span></div>}
              {addForm.notes && <div style={{ gridColumn: '1/-1' }}><span style={{ color: 'var(--text-tertiary)' }}>Notes : </span><span>{addForm.notes}</span></div>}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button onClick={() => upload(pendingFile)} style={{ flex: 1, minWidth: 140, padding: '0.55rem 1rem', borderRadius: '8px', background: 'var(--primary)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>✅ Enregistrer</button>
              {previewUrl && <button onClick={() => setShowPreview(true)} style={{ padding: '0.55rem 0.9rem', borderRadius: '8px', background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}>👁 Aperçu</button>}
              <button onClick={resetPending} style={{ padding: '0.55rem 0.9rem', borderRadius: '8px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.88rem' }}>✕ Annuler</button>
            </div>
          </div>
        )}

        {/* Zone drag & drop */}
        <div
          onDragOver={e => { e.preventDefault(); if (!analysing && !uploading) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) analyserDocument(f); }}
          onClick={() => { if (!analysing && !uploading) inputRef.current.click(); }}
          style={{ border: `2px dashed ${dragOver ? '#E87722' : pendingFile ? 'var(--primary)' : 'var(--border)'}`, borderRadius: '10px', padding: '1.5rem 1rem', textAlign: 'center', cursor: (analysing || uploading) ? 'default' : 'pointer', background: dragOver ? 'rgba(232,119,34,0.05)' : 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '0.88rem', transition: 'all 0.15s' }}>
          <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) analyserDocument(e.target.files[0]); }} />
          {uploading ? <span>⏳ Envoi en cours...</span>
            : analysing ? <><div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>🤖</div><div><strong>Analyse IA en cours...</strong></div></>
            : pendingFile ? <><div style={{ fontSize: '1.2rem', marginBottom: '0.2rem' }}>📄</div><div><strong>{pendingFile.name}</strong></div><div style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>Cliquez pour changer de fichier</div></>
            : <><div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>📁</div><div><strong>Cliquez ou déposez un fichier ici</strong></div><div style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>PDF, images, Word, Excel...</div></>
          }
        </div>
      </div>

      {/* Liste des documents */}
      {loading ? (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>Chargement...</p>
      ) : docs.length === 0 ? (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', padding: '0.5rem 0' }}>Aucun document enregistré pour ce stagiaire.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {docs.map(doc => {
            const statut = docStatutStagiaire(doc.date_fin_validite);
            return (
              <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', padding: '0.55rem 0.7rem', borderRadius: '7px', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                {editDoc?.id === doc.id ? (
                  <div style={{ flex: 1, display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <select value={editDoc.type_document} onChange={e => setEditDoc({ ...editDoc, type_document: e.target.value })} style={{ ...inputStyle, flex: '1 1 160px' }}>
                      {typesDocs.map(t => <option key={t}>{t}</option>)}
                    </select>
                    <input type="date" value={editDoc.date_emission ? String(editDoc.date_emission).slice(0,10) : ''} onChange={e => setEditDoc({ ...editDoc, date_emission: e.target.value })} style={{ ...inputStyle, flex: '0 0 140px' }} placeholder="Date émission" />
                    <input type="date" value={editDoc.date_fin_validite ? String(editDoc.date_fin_validite).slice(0,10) : ''} onChange={e => setEditDoc({ ...editDoc, date_fin_validite: e.target.value })} style={{ ...inputStyle, flex: '0 0 140px' }} />
                    <input value={editDoc.notes || ''} onChange={e => setEditDoc({ ...editDoc, notes: e.target.value })} placeholder="Notes..." style={{ ...inputStyle, flex: '1 1 120px' }} />
                    <button className="btn-primary" onClick={saveEdit} style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}>✓</button>
                    <button className="btn-secondary" onClick={() => setEditDoc(null)} style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}>✕</button>
                  </div>
                ) : (
                  <>
                    <span style={{ fontSize: '1.1rem' }}>{doc.nom_original?.endsWith('.pdf') ? '📄' : doc.nom_original?.match(/\.(jpe?g|png|gif)$/i) ? '🖼️' : '📎'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <button onClick={() => ouvrirDocument({ titre: doc.nom_original, url: docUrl(doc.id), nomFichier: doc.nom_original })} style={{ color: 'var(--accent-blue)', fontWeight: 600, fontSize: '0.85rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>{doc.nom_original}</button>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '1px' }}>
                        <Badge style={{ fontSize: '0.72rem' }}>{doc.type_document || 'Autre'}</Badge>
                        {doc.session_id && (
                          <Link to={`/sessions/${doc.session_id}`} style={{ marginLeft: '0.4rem', background: 'rgba(59,130,246,0.12)', color: 'var(--accent-blue)', borderRadius: '4px', padding: '0.1rem 0.4rem', fontSize: '0.72rem', textDecoration: 'none', fontWeight: 600 }}
                            title={doc.session_intitule || doc.session_id}>
                            📋 {doc.session_id}
                          </Link>
                        )}
                        {' '}{fmtTaille(doc.taille)}
                        {doc.date_emission && ` · Émis le ${formatDate(doc.date_emission)}`}
                        {doc.notes && ` · ${doc.notes}`}
                      </div>
                    </div>
                    {statut && <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem', borderRadius: '12px', background: statut.bg, color: statut.color, whiteSpace: 'nowrap' }}>{statut.label}</span>}
                    {!doc.date_fin_validite && <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Pas de date de validité</span>}
                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                      <button onClick={() => setEditDoc({ ...doc })} style={{ padding: '0.25rem 0.55rem', borderRadius: '5px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}>✏</button>
                      <button onClick={() => telechargerDocument({ url: docUrl(doc.id), nomFichier: doc.nom_original })} title="Télécharger" style={{ padding: '0.25rem 0.55rem', borderRadius: '5px', background: 'rgba(59,130,246,0.1)', border: '1px solid var(--accent-blue)', color: 'var(--accent-blue)', cursor: 'pointer', fontSize: '0.75rem' }}>⬇</button>
                      <button onClick={() => supprimerDoc(doc.id)} style={{ padding: '0.25rem 0.55rem', borderRadius: '5px', background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>🗑</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal aperçu */}
      {showPreview && previewUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', flexDirection: 'column' }} onClick={e => { if (e.target === e.currentTarget) setShowPreview(false); }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.5rem', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>👁 Aperçu — {pendingFile?.name}</span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => upload(pendingFile)} style={{ padding: '0.4rem 1rem', borderRadius: '6px', border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>✅ Enregistrer</button>
              <button onClick={() => setShowPreview(false)} style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>✕ Fermer</button>
            </div>
          </div>
          {pendingFile?.type?.startsWith('image/')
            ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflow: 'auto' }}><img src={previewUrl} alt="aperçu" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }} /></div>
            : <iframe src={previewUrl} title="aperçu" style={{ flex: 1, border: 'none', background: '#fff' }} />
          }
        </div>
      )}
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

function buildAttestationNom(abreviation, nom_dossier, nom, prenoms, date_emission, ext) {
  const raw = date_emission || new Date().toISOString().slice(0,10);
  const [y, m, d] = raw.slice(0,10).split('-');
  const date = `${d}-${m}-${y}`;
  const nomMaj = (nom || '').toUpperCase();
  const prenom = prenoms || '';
  const abrv = (abreviation || '').toUpperCase().replace(/\s+/g, '_');
  const court = (nom_dossier || '').replace(/\s+/g, '_');
  return `${abrv}_${court}_${nomMaj} ${prenom}_${date}${ext}`;
}

function SectionAttestations({ stagiaireId, stagiaire }) {
  const { token } = useAuth();
  const inputRef = useRef();

  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [typesDocs, setTypesDocs] = useState([]); // filtered by abreviation
  const [typesMap, setTypesMap] = useState({});   // nom → { abreviation, duree_mois }
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const sessions = (stagiaire?.sessions || []).filter(s => !s.annulee);
  const inputStyle = { padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };

  const [form, setForm] = useState({
    type_document: '',
    session_id: '',
    date_emission: '',
    date_fin_validite: '',
  });

  useEffect(() => {
    fetch(`${API_BASE}/parametres`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        const raw = data?.types_docs_stagiaires;
        if (raw) {
          try {
            const arr = JSON.parse(raw);
            const avecAbrv = arr
              .map(item => typeof item === 'string' ? { nom: item, abreviation: '', duree_mois: null } : { abreviation: '', ...item })
              .filter(item => item.abreviation && item.abreviation.trim());
            const noms = avecAbrv.map(i => i.nom);
            setTypesDocs(noms);
            const map = {};
            avecAbrv.forEach(i => { map[i.nom] = i; });
            setTypesMap(map);
            if (noms.length) setForm(f => ({ ...f, type_document: noms[0] }));
          } catch {}
        }
      }).catch(() => {});
  }, [API_BASE, token]);

  const chargerDocs = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents?categorie=attestation`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setDocs(Array.isArray(data) ? data : []);
    } catch { setDocs([]); }
    finally { setLoadingDocs(false); }
  }, [stagiaireId, token, API_BASE]);

  useEffect(() => { chargerDocs(); }, [chargerDocs]);

  function onSessionChange(session_id) {
    const s = sessions.find(x => x.session_id === session_id);
    const date_emission = s?.date_fin ? String(s.date_fin).slice(0,10) : '';
    let date_fin_validite = '';
    if (s?.recyclage && s?.periode_recyclage_mois && date_emission) {
      try {
        const d = new Date(date_emission);
        d.setMonth(d.getMonth() + parseInt(s.periode_recyclage_mois));
        date_fin_validite = d.toISOString().slice(0,10);
      } catch {}
    }
    setForm(f => ({ ...f, session_id, date_emission, date_fin_validite }));
  }

  function ouvrirApercu(doc) {
    ouvrirDocument({ titre: doc.nom_original, url: `/stagiaires/${stagiaireId}/documents/${doc.id}/download`, nomFichier: doc.nom_original });
  }

  function resetPending() {
    setPendingFile(null);
    setShowPreview(false);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  }

  function getSelectedSession() {
    return sessions.find(s => s.session_id === form.session_id);
  }

  async function upload(file) {
    if (!form.type_document) { alert('Sélectionnez un type de document.'); return; }
    if (!form.session_id) { alert('Sélectionnez une session.'); return; }
    setUploading(true);
    try {
      const sel = getSelectedSession();
      const extMatch = file.name.match(/(\.[^.]+)$/);
      const ext = extMatch ? extMatch[1].toLowerCase() : '';
      const abrvInfo = typesMap[form.type_document] || {};
      const nomRenomme = buildAttestationNom(
        abrvInfo.abreviation,
        sel?.nom_dossier || sel?.intitule_formation || '',
        stagiaire.nom,
        stagiaire.prenoms,
        form.date_emission,
        ext
      );
      const fd = new FormData();
      fd.append('fichier', file);
      fd.append('type_document', form.type_document);
      fd.append('session_id', form.session_id);
      fd.append('categorie', 'attestation');
      fd.append('nom_original_custom', nomRenomme);
      if (form.date_emission)     fd.append('date_emission', form.date_emission);
      if (form.date_fin_validite) fd.append('date_fin_validite', form.date_fin_validite);
      const r = await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setDocs(prev => [data, ...prev]);
      setForm(f => ({ ...f, date_fin_validite: '', date_emission: '' }));
      resetPending();
    } catch(e) { alert('Erreur upload : ' + e.message); }
    finally { setUploading(false); }
  }

  async function supprimerDoc(docId) {
    if (!window.confirm('Supprimer cette attestation ?')) return;
    try {
      await fetch(`${API_BASE}/stagiaires/${stagiaireId}/documents/${docId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch(e) { alert('Erreur : ' + e.message); }
  }


  const previewFilename = (() => {
    if (!form.type_document || !form.session_id) return null;
    const sel = getSelectedSession();
    const abrvInfo = typesMap[form.type_document] || {};
    return buildAttestationNom(
      abrvInfo.abreviation,
      sel?.nom_dossier || sel?.intitule_formation || '',
      stagiaire.nom, stagiaire.prenoms, form.date_emission, '.pdf'
    );
  })();

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <h3 className="card-title">🏅 Attestations</h3>

      {typesDocs.length === 0 ? (
        <div style={{ padding: '1rem', color: 'var(--text-tertiary)', fontSize: '0.85rem', background: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border)' }}>
          Aucun type de document avec "Abrégé" défini. Ajoutez un abrégé dans <strong>Paramètres → Types de documents stagiaires</strong>.
        </div>
      ) : (
        <div style={{ marginBottom: '0.8rem' }}>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
            Déposez ici les attestations signées par CEJ et le formateur. Le fichier sera renommé automatiquement.
          </p>
          <div className="grid-2" style={{ gap: '0 0.8rem', marginBottom: '0.6rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Type de document</label>
              <select value={form.type_document} onChange={e => setForm(f => ({ ...f, type_document: e.target.value }))} style={inputStyle}>
                {typesDocs.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Session</label>
              <select value={form.session_id} onChange={e => onSessionChange(e.target.value)} style={inputStyle}>
                <option value="">— Sélectionner une session —</option>
                {sessions.map(s => (
                  <option key={s.session_id} value={s.session_id}>
                    {s.session_id} — {s.intitule_formation}{s.date_debut ? ` (${formatDate(s.date_debut)})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Date d'émission</label>
              <input type="date" value={form.date_emission} onChange={e => setForm(f => ({ ...f, date_emission: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>Date de fin de validité <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(optionnelle)</span></label>
              <input type="date" value={form.date_fin_validite} onChange={e => setForm(f => ({ ...f, date_fin_validite: e.target.value }))} style={inputStyle} />
            </div>
          </div>

          {previewFilename && (
            <div style={{ marginBottom: '0.6rem', padding: '0.5rem 0.75rem', background: 'rgba(232,119,34,0.06)', border: '1px solid rgba(232,119,34,0.25)', borderRadius: '7px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Nom du fichier après enregistrement : <strong style={{ color: 'var(--text-primary)', fontFamily: 'monospace' }}>{previewFilename}</strong>
            </div>
          )}

          {pendingFile && !uploading && (
            <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '0.9rem 1rem', marginBottom: '0.8rem', background: 'var(--bg-elevated)' }}>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                Fichier sélectionné : <strong>{pendingFile.name}</strong> ({fmtTaille(pendingFile.size)})
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={() => upload(pendingFile)} style={{ flex: 1, minWidth: 140, padding: '0.55rem 1rem', borderRadius: '8px', background: 'var(--primary)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem' }}>✅ Enregistrer</button>
                {previewUrl && <button onClick={() => setShowPreview(true)} style={{ padding: '0.55rem 0.9rem', borderRadius: '8px', background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}>👁 Aperçu</button>}
                <button onClick={resetPending} style={{ padding: '0.55rem 0.9rem', borderRadius: '8px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.88rem' }}>✕ Annuler</button>
              </div>
            </div>
          )}

          <div
            onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) { setPendingFile(f); const u = f.type === 'application/pdf' || f.type.startsWith('image/') ? URL.createObjectURL(f) : null; if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(u); } }}
            onClick={() => { if (!uploading) inputRef.current.click(); }}
            style={{ border: `2px dashed ${dragOver ? '#E87722' : pendingFile ? 'var(--primary)' : 'var(--border)'}`, borderRadius: '10px', padding: '1.5rem 1rem', textAlign: 'center', cursor: uploading ? 'default' : 'pointer', background: dragOver ? 'rgba(232,119,34,0.05)' : 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '0.88rem', transition: 'all 0.15s' }}>
            <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) { const f = e.target.files[0]; setPendingFile(f); const u = f.type === 'application/pdf' || f.type.startsWith('image/') ? URL.createObjectURL(f) : null; if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(u); } }} />
            {uploading ? <span>⏳ Envoi en cours...</span>
              : pendingFile ? <><div style={{ fontSize: '1.2rem', marginBottom: '0.2rem' }}>📄</div><div><strong>{pendingFile.name}</strong></div><div style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>Cliquez pour changer de fichier</div></>
              : <><div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>📁</div><div><strong>Cliquez ou déposez l'attestation scannée ici</strong></div><div style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>PDF ou image (JPG, PNG)</div></>
            }
          </div>
        </div>
      )}

      {/* Liste des attestations */}
      {loadingDocs ? (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>Chargement...</p>
      ) : docs.length === 0 ? (
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', padding: '0.5rem 0' }}>Aucune attestation enregistrée pour ce stagiaire.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {docs.map(doc => (
            <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', padding: '0.55rem 0.7rem', borderRadius: '7px', background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
              <span style={{ fontSize: '1.1rem' }}>🏅</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <button onClick={() => ouvrirApercu(doc)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent-blue)', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', textAlign: 'left' }}>{doc.nom_original}</button>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginTop: '1px' }}>
                  <Badge style={{ fontSize: '0.72rem' }}>{doc.type_document || 'Attestation'}</Badge>
                  {doc.session_id && (
                    <Link to={`/sessions/${doc.session_id}`} style={{ marginLeft: '0.4rem', background: 'rgba(59,130,246,0.12)', color: 'var(--accent-blue)', borderRadius: '4px', padding: '0.1rem 0.4rem', fontSize: '0.72rem', textDecoration: 'none', fontWeight: 600 }}
                      title={doc.session_intitule || doc.session_id}>
                      📋 {doc.session_id}
                    </Link>
                  )}
                  {' '}{fmtTaille(doc.taille)}
                  {doc.date_emission && ` · Émis le ${formatDate(doc.date_emission)}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                <button onClick={() => ouvrirApercu(doc)} title="Aperçu" style={{ padding: '0.25rem 0.55rem', borderRadius: '5px', background: 'rgba(59,130,246,0.1)', border: '1px solid var(--accent-blue)', color: 'var(--accent-blue)', cursor: 'pointer', fontSize: '0.75rem' }}>👁</button>
                <button onClick={() => supprimerDoc(doc.id)} style={{ padding: '0.25rem 0.55rem', borderRadius: '5px', background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem' }}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}


      {showPreview && previewUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', flexDirection: 'column' }} onClick={e => { if (e.target === e.currentTarget) setShowPreview(false); }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.5rem', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>👁 Aperçu — {pendingFile?.name}</span>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={() => upload(pendingFile)} style={{ padding: '0.4rem 1rem', borderRadius: '6px', border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>✅ Enregistrer</button>
              <button onClick={() => setShowPreview(false)} style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>✕ Fermer</button>
            </div>
          </div>
          {pendingFile?.type?.startsWith('image/')
            ? <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflow: 'auto' }}><img src={previewUrl} alt="aperçu" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }} /></div>
            : <iframe src={previewUrl} title="aperçu" style={{ flex: 1, border: 'none', background: '#fff' }} />
          }
        </div>
      )}
    </div>
  );
}

function ModalModifier({ stagiaire, clientsData, onSave, onClose }) {
  const { token } = useAuth();
  const todayIso = new Date().toISOString().substring(0, 10);
  const [form, setForm] = useState({
    civilite: stagiaire.civilite || '',
    nom: stagiaire.nom || '',
    nom_jeune_fille: stagiaire.nom_jeune_fille || '',
    prenoms: stagiaire.prenoms || '',
    date_naissance: stagiaire.date_naissance
      ? String(stagiaire.date_naissance).substring(0, 10)
      : '',
    date_creation: stagiaire.date_creation
      ? String(stagiaire.date_creation).substring(0, 10)
      : todayIso,
    client_id: stagiaire.client_id || '',
    email: stagiaire.email || '',
    tel: stagiaire.tel || '',
    num_securite_sociale: stagiaire.num_securite_sociale || '',
    actif: stagiaire.actif ?? true,
    notes: stagiaire.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/stagiaires/${stagiaire.stagiaire_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onSave(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', width: '460px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <h3 style={{ marginBottom: '0.25rem', color: 'var(--text-primary)' }}>Modifier le stagiaire</h3>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', marginBottom: '1.5rem' }}>
          ID : <strong>{stagiaire.stagiaire_id}</strong>
        </p>

        {/* Civilité + Nom */}
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '0 0.5rem', marginBottom: '0.8rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Civilité</label>
            <select value={form.civilite} onChange={e => setForm({ ...form, civilite: e.target.value })}
              style={{ width: '100%', padding: '0.5rem 0.4rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
              <option value="">—</option>
              <option value="M.">M.</option>
              <option value="Mme">Mme</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Nom d'usage *</label>
            <input type="text" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })}
              style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' }} />
          </div>
        </div>
        <div style={{ marginBottom: '0.8rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Prénoms</label>
          <input type="text" value={form.prenoms} onChange={e => setForm({ ...form, prenoms: e.target.value })}
            style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: '0.8rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Nom de naissance</label>
          <input type="text" value={form.nom_jeune_fille} onChange={e => setForm({ ...form, nom_jeune_fille: e.target.value })}
            style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' }} />
        </div>

        {/* Date de naissance */}
        <div style={{ marginBottom: '0.8rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Date de naissance</label>
          <input type="date" value={form.date_naissance} onChange={e => setForm({ ...form, date_naissance: e.target.value })}
            style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' }} />
        </div>

        {[
          { label: 'Email', key: 'email', type: 'email' },
          { label: 'Téléphone', key: 'tel', type: 'text' },
          { label: 'N° Sécurité sociale', key: 'num_securite_sociale', type: 'text' },
          { label: 'Date de création de la fiche', key: 'date_creation', type: 'date' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: '0.8rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>{f.label}</label>
            <input
              type={f.type} value={form[f.key]}
              onChange={e => setForm({ ...form, [f.key]: e.target.value })}
              style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box' }}
            />
          </div>
        ))}

        <div style={{ marginBottom: '0.8rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Entreprise</label>
          <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}
            style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
            <option value="">— Aucune —</option>
            {clientsData?.data?.map(c => (
              <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input type="checkbox" id="actif" checked={form.actif} onChange={e => setForm({ ...form, actif: e.target.checked })} />
          <label htmlFor="actif" style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Stagiaire actif</label>
        </div>

        <div style={{ marginBottom: '0.8rem' }}>
          <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>Notes</label>
          <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={4}
            style={{ width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: '0.9rem', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '0.5rem 0.8rem', marginBottom: '1rem', color: '#f87171', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Annuler</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalArchiver({ stagiaire, onConfirm, onClose }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '12px', padding: '2rem', width: '400px', maxWidth: '90vw' }}>
        <h3 style={{ marginBottom: '0.5rem', color: '#f87171' }}>Archiver le stagiaire</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
          Vous êtes sur le point d'archiver le stagiaire :
        </p>
        <div style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '0.8rem 1rem', marginBottom: '1.5rem' }}>
          <strong style={{ color: 'var(--text-primary)' }}>{`${stagiaire.civilite||''} ${stagiaire.prenoms||''} ${stagiaire.nom||''}`.trim()}</strong>
          <span style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', marginLeft: '0.5rem' }}>{stagiaire.stagiaire_id}</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
          Le stagiaire sera masqué des listes mais ses données (inscriptions, présences) seront conservées. Il pourra être restauré à tout moment.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} disabled={confirming}>Annuler</button>
          <button
            onClick={async () => { setConfirming(true); await onConfirm(); setConfirming(false); }}
            disabled={confirming}
            style={{ padding: '0.5rem 1rem', borderRadius: '8px', background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', opacity: confirming ? 0.7 : 1 }}>
            {confirming ? 'Archivage...' : '🗑 Archiver'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StagiaireDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, hasRole } = useAuth();
  const [modal, setModal] = useState(null); // null | 'modifier' | 'supprimer'
  const [dataOverride, setDataOverride] = useState(null);
  const [docTab, setDocTab] = useState('administratif');

  const navState = location.state || null;
  const navIds = navState?.ids || [];
  const currentNavIndex = navIds.indexOf(id);
  const prevId = currentNavIndex > 0 ? navIds[currentNavIndex - 1] : null;
  const nextId = currentNavIndex >= 0 && currentNavIndex < navIds.length - 1 ? navIds[currentNavIndex + 1] : null;

  function navigateTo(targetId) {
    navigate(`/stagiaires/${targetId}`, { state: navState });
  }

  const { data: rawData, loading } = useApi(`/stagiaires/${id}`);
  const { data: clientsData } = useApi('/clients', { limit: 200 }, []);
  const data = dataOverride || rawData;

  if (loading || !data) return <Loading />;

  const nbSessions = data.sessions?.length || 0;
  const nbPresents = data.sessions?.reduce((acc, s) => acc + (parseInt(s.nb_presents) || 0), 0) || 0;
  const nbCreneaux = data.sessions?.reduce((acc, s) => acc + (parseInt(s.nb_creneaux) || 0), 0) || 0;
  const tauxGlobal = nbCreneaux ? Math.round(100 * nbPresents / nbCreneaux) : 0;

  async function handleAssocierClient(clientDeduit) {
    if (!clientDeduit?.client_id) {
      alert('Ce client n\'existe pas encore dans la base clients. Ajoutez-le d\'abord.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/stagiaires/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          civilite: data.civilite,
          nom: data.nom,
          nom_jeune_fille: data.nom_jeune_fille,
          prenoms: data.prenoms,
          client_id: clientDeduit.client_id,
          email: data.email,
          tel: data.tel,
          num_securite_sociale: data.num_securite_sociale,
          actif: data.actif,
        }),
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated.error);
      setDataOverride({
        ...data,
        client_id: clientDeduit.client_id,
        nom_client: clientDeduit.nom_client,
        client_ville: clientDeduit.ville,
      });
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  }

  async function handleArchiver() {
    try {
      await apiFetch(`/stagiaires/${id}`, { method: 'DELETE' });
      navigate('/stagiaires');
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  }

  async function handleRestaurer() {
    try {
      await apiFetch(`/stagiaires/${id}/restaurer`, { method: 'POST' });
      window.location.reload();
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  }

  const canEdit = hasRole('utilisateur');

  return (
    <div className="page">
      <PageHeader
        title={[data.civilite, data.prenoms, data.nom, data.nom_jeune_fille && data.nom_jeune_fille !== data.nom ? `née ${data.nom_jeune_fille}` : ''].filter(Boolean).join(' ')}
        subtitle={`${data.stagiaire_id}${data.nom_client ? ` — ${data.nom_client}` : ''}`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <BackButton />
            {navIds.length > 0 && (
              <>
                <button
                  className="btn-secondary"
                  onClick={() => prevId && navigateTo(prevId)}
                  disabled={!prevId}
                  style={{ padding: '0.5rem 0.8rem' }}
                  title="Fiche précédente">
                  ◀
                </button>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', minWidth: '40px', textAlign: 'center' }}>
                  {currentNavIndex + 1} / {navIds.length}
                </span>
                <button
                  className="btn-secondary"
                  onClick={() => nextId && navigateTo(nextId)}
                  disabled={!nextId}
                  style={{ padding: '0.5rem 0.8rem' }}
                  title="Fiche suivante">
                  ▶
                </button>
              </>
            )}
            {canEdit && (
              <>
                {!data.actif && (
                  <span style={{ padding: '0.3rem 0.8rem', borderRadius: '20px', background: 'rgba(239,68,68,0.12)', color: '#f87171', fontSize: '0.8rem', fontWeight: 600 }}>ARCHIVÉ</span>
                )}
                <button className="btn-secondary" onClick={() => setModal('modifier')}>✏️ Modifier</button>
                {data.actif !== false ? (
                  <button
                    onClick={() => setModal('archiver')}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                    🗑 Archiver
                  </button>
                ) : (
                  <button
                    onClick={handleRestaurer}
                    style={{ padding: '0.5rem 1rem', borderRadius: '8px', background: 'transparent', border: '1px solid var(--accent-emerald)', color: 'var(--accent-emerald)', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
                    ↩ Restaurer
                  </button>
                )}
              </>
            )}
          </div>
        }
      />

      <div className="detail-grid">
        <div className="card">
          <h3 className="card-title">Informations générales</h3>
          <div className="info-grid">
            <div className="info-row"><label>ID</label><span>{data.stagiaire_id}</span></div>
            <div className="info-row"><label>Civilité</label><span>{data.civilite || '—'}</span></div>
            <div className="info-row"><label>Nom d'usage</label><span><strong>{data.nom || '—'}</strong></span></div>
            <div className="info-row"><label>Nom de naissance</label><span>{data.nom_jeune_fille || '—'}</span></div>
            <div className="info-row"><label>Prénoms</label><span>{data.prenoms || '—'}</span></div>
            {data.date_naissance && (
              <div className="info-row"><label>Date de naissance</label><span>{formatDate(data.date_naissance)}</span></div>
            )}
            <div className="info-row">
              <label>Entreprise</label>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                {data.nom_client
                  ? <strong>{data.nom_client}</strong>
                  : data.client_deduit
                    ? (
                      <>
                        <span style={{ color: 'var(--text-tertiary)' }}>Non renseigné</span>
                        <span style={{ fontSize: '0.75rem', color: '#f59e0b', padding: '0.1rem 0.5rem', background: 'rgba(245,158,11,0.1)', borderRadius: '4px' }}>
                          Déduit : {data.client_deduit.nom_client_snapshot}
                        </span>
                        {canEdit && (
                          <button
                            onClick={() => handleAssocierClient(data.client_deduit)}
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '4px', background: 'rgba(59,130,246,0.1)', border: '1px solid var(--accent-blue)', color: 'var(--accent-blue)', cursor: 'pointer' }}>
                            🔗 Associer
                          </button>
                        )}
                      </>
                    )
                    : '—'
                }
              </span>
            </div>
            <div className="info-row"><label>Ville</label><span>{data.client_ville || '—'}</span></div>
            <div className="info-row"><label>Email</label>
              <span>{data.email
                ? <a href={`mailto:${data.email}`} style={{ color: 'var(--accent-blue)' }}>{data.email}</a>
                : '—'}
              </span>
            </div>
            <div className="info-row"><label>Téléphone</label><span>{data.tel || '—'}</span></div>
            <div className="info-row"><label>N° Sécurité sociale</label><span>{data.num_securite_sociale || '—'}</span></div>
            <div className="info-row"><label>Date de création</label><span>{formatDate(data.date_creation)}</span></div>
            <div className="info-row"><label>Statut</label>
              <span>{data.actif
                ? <Badge variant="badge-success">Actif</Badge>
                : <Badge variant="badge-danger">Inactif</Badge>}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="card-title">Statistiques</h3>
          <div className="info-grid">
            <div className="info-row"><label>Sessions suivies</label><span><strong>{nbSessions}</strong></span></div>
            <div className="info-row"><label>Créneaux total</label><span>{nbCreneaux}</span></div>
            <div className="info-row"><label>Présences</label><span>{nbPresents}</span></div>
            <div className="info-row"><label>Taux de présence global</label>
              <span className="presence-rate">
                <span className="presence-bar" style={{ width: `${tauxGlobal}%` }} />
                <span className="presence-text">{tauxGlobal}%</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {data.notes && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 className="card-title">Notes</h3>
          <p style={{ whiteSpace: 'pre-wrap', color: 'var(--text-primary)', margin: 0 }}>{data.notes}</p>
        </div>
      )}

      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 className="card-title">Sessions suivies ({nbSessions})</h3>
        {nbSessions === 0 ? (
          <p style={{ color: 'var(--text-tertiary)', padding: '1rem 0' }}>Aucune session trouvée pour ce stagiaire.</p>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Session</th>
                <th>Formation</th>
                <th>Dates</th>
                <th>Durée</th>
                <th>Formateur</th>
                <th>Groupe</th>
                <th>Client</th>
                <th>Présence</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.sessions.map((s, i) => {
                const taux = parseInt(s.taux_presence) || 0;
                return (
                  <tr key={i} style={{ cursor: 'pointer' }} onClick={() => navigate(`/sessions/${s.session_id}`)}>
                    <td><strong style={{ color: 'var(--accent-blue)' }}>{s.session_id}</strong></td>
                    <td>{s.intitule_formation}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                      {formatDate(s.date_debut)}{s.date_fin && s.date_fin !== s.date_debut ? ` → ${formatDate(s.date_fin)}` : ''}
                    </td>
                    <td>{s.duree ? `${s.duree}h` : '—'}</td>
                    <td style={{ fontSize: '0.85rem' }}>{s.nom_formateur || '—'}</td>
                    <td>{s.groupe_snapshot ? <Badge>{s.groupe_snapshot}</Badge> : '—'}</td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{s.nom_client_snapshot || '—'}</td>
                    <td>
                      <span className="presence-rate" style={{ minWidth: '80px' }}>
                        <span className="presence-bar" style={{
                          width: `${taux}%`,
                          background: taux >= 80 ? 'var(--accent-emerald)' : taux >= 50 ? '#f59e0b' : '#ef4444'
                        }} />
                        <span className="presence-text">
                          {s.nb_creneaux > 0 ? `${taux}% (${s.nb_presents}/${s.nb_creneaux})` : '—'}
                        </span>
                      </span>
                    </td>
                    <td>
                      {s.annulee
                        ? <Badge variant="badge-danger">Annulée</Badge>
                        : <Badge variant="badge-success">Active</Badge>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Onglets Documents / Attestations */}
      <div style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.2rem', borderBottom: '1px solid var(--border)' }}>
          {[['administratif', '📎 Documents administratifs'], ['attestations', '🏅 Attestations']].map(([key, label]) => (
            <button key={key} onClick={() => setDocTab(key)} style={{
              padding: '0.5rem 1.1rem', borderRadius: '8px 8px 0 0',
              border: '1px solid var(--border)',
              borderBottom: docTab === key ? '1px solid var(--bg-card)' : '1px solid var(--border)',
              background: docTab === key ? 'var(--bg-card)' : 'var(--bg-elevated)',
              color: docTab === key ? 'var(--text-primary)' : 'var(--text-tertiary)',
              fontWeight: docTab === key ? 700 : 400, fontSize: '0.85rem',
              cursor: 'pointer', marginBottom: '-1px', transition: 'all 0.12s',
            }}>{label}</button>
          ))}
        </div>
        {docTab === 'administratif'
          ? <SectionDocuments stagiaireId={id} stagiaire={data} />
          : <SectionAttestations stagiaireId={id} stagiaire={data} />
        }
      </div>

      {modal === 'modifier' && (
        <ModalModifier
          stagiaire={data}
          clientsData={clientsData}
          onSave={updated => { setDataOverride({ ...data, ...updated }); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'archiver' && (
        <ModalArchiver
          stagiaire={data}
          onConfirm={handleArchiver}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
